import {
  API_PREFIX,
  type ApiErrorBody,
  CLIENT_TYPE_HEADER,
  type CurrentUser,
  type LoginResponse,
} from '@brimostech/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  get code() {
    return this.body.error;
  }

  get details() {
    return this.body.details ?? [];
  }
}

const NETWORK_ERROR: ApiErrorBody = {
  statusCode: 0,
  error: 'NETWORK_ERROR',
  message: 'Sem conexão com o servidor. Verifique sua internet e tente de novo.',
};

const GENERIC_ERROR = (status: number): ApiErrorBody => ({
  statusCode: status,
  error: 'ERROR',
  message: 'Não foi possível concluir a operação. Tente novamente.',
});

type SessionListener = (user: CurrentUser | null) => void;

let accessToken: string | null = null;
let refreshInFlight: Promise<LoginResponse | null> | null = null;
const listeners = new Set<SessionListener>();
const SESSION_HINT_KEY = 'condominio.session';

/** Whether a refresh cookie probably exists here: set at login, cleared when the session is lost. */
export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function setSessionHint(present: boolean) {
  try {
    if (present) localStorage.setItem(SESSION_HINT_KEY, '1');
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // Storage unavailable: the boot refresh simply runs as before.
  }
}

function notify(user: CurrentUser | null) {
  setSessionHint(user !== null);
  for (const listener of listeners) listener(user);
}

/** In-memory access token + session events. The refresh token lives in an httpOnly cookie. */
export const session = {
  getAccessToken: () => accessToken,
  setAccessToken(token: string | null) {
    accessToken = token;
    setSessionHint(token !== null);
  },
  /** Notified when a refresh succeeds (user) or the session is lost (null). */
  subscribe(listener: SessionListener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

function baseHeaders(): Record<string, string> {
  return { Accept: 'application/json', [CLIENT_TYPE_HEADER]: 'web' };
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiErrorBody).message === 'string' &&
    typeof (value as ApiErrorBody).error === 'string'
  );
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Send the access token (default true). */
  auth?: boolean;
  /** Try one silent refresh on 401 (default true). */
  retryOn401?: boolean;
  signal?: AbortSignal;
}

/** Typed fetch wrapper: JSON in/out, bearer token, one transparent refresh on 401. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, retryOn401 = true, signal } = options;
  const headers = baseHeaders();
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new ApiError(0, NETWORK_ERROR);
  }

  if (response.status === 401 && auth && retryOn401) {
    const refreshed = await refreshSession();
    if (refreshed) return api<T>(path, { ...options, retryOn401: false });
  }

  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && auth) {
      accessToken = null;
      notify(null);
    }
    throw new ApiError(
      response.status,
      isApiErrorBody(data) ? data : GENERIC_ERROR(response.status),
    );
  }
  return data as T;
}

/** Exchanges the httpOnly refresh cookie for a new access token. Only one refresh runs at a time. */
export function refreshSession(): Promise<LoginResponse | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<LoginResponse | null> {
  try {
    const response = await fetch(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { ...baseHeaders(), 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'same-origin',
    });
    if (!response.ok) {
      accessToken = null;
      notify(null);
      return null;
    }
    const data = (await response.json()) as LoginResponse;
    if (!data.tokens || !data.user) {
      accessToken = null;
      notify(null);
      return null;
    }
    accessToken = data.tokens.accessToken;
    notify(data.user);
    return data;
  } catch {
    return null;
  }
}

/** Builds a query string, skipping empty values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
